import { isValidElement, type KeyboardEventHandler, type Ref } from "react";
import { PickerCellInputTrigger } from "./PickerCellInputTrigger";
import { PickerInputTrigger } from "./PickerInputTrigger";
import type {
    PickerChevron,
    PickerInputTriggerRootProps,
    PickerInputTriggerSize,
    PickerTriggerElement,
    PickerTriggerPreset,
} from "./Picker.types";

/**
 * Button, input and attached triggers show their chevron at rest; cell presets show
 * none (legacy cell geometry, decision 2026-09-25) and an icon-only `Button`
 * keeps its glyph alone.
 */
export const defaultChevronForPreset = (
    trigger: PickerTriggerPreset | PickerTriggerElement | undefined,
): PickerChevron => {
    if (isValidElement(trigger)) {
        return trigger.props.iconOnly === true ? "none" : "always";
    }
    return trigger === "cell" || trigger === "cellInput" ? "none" : "always";
};

/** True for the presets that render an `<input>` as the trigger. */
export const isInputTriggerPreset = (
    trigger: PickerTriggerPreset | PickerTriggerElement | undefined,
): trigger is PickerInputTriggerRootProps["trigger"] =>
    trigger === "input" || trigger === "cellInput";

/** The input preset a data-driven root renders: the Input field or the chromeless cell input. */
export const PickerInputTriggerPreset = ({
    trigger,
    placeholder,
    readOnly,
    size,
    clearable,
    className,
    ref,
    onKeyDown,
}: {
    trigger: "input" | "cellInput";
    placeholder?: string;
    readOnly: boolean;
    size?: PickerInputTriggerSize;
    clearable?: boolean;
    className?: string;
    ref?: Ref<HTMLInputElement>;
    onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
}) =>
    trigger === "cellInput" ? (
        <PickerCellInputTrigger
            placeholder={placeholder}
            readOnly={readOnly}
            clearable={clearable}
            className={className}
            ref={ref}
            onKeyDown={onKeyDown}
        />
    ) : (
        <PickerInputTrigger
            placeholder={placeholder}
            size={size}
            clearable={clearable}
            className={className}
            ref={ref}
            onKeyDown={onKeyDown}
        />
    );
